/**
 * Single source of truth for the new-password validation criteria.
 *
 * A password is valid when it meets BOTH:
 *   1. (mandatory) its length is between PASSWORD_MIN_LENGTH and PASSWORD_MAX_LENGTH.
 *   2. it satisfies at least PASSWORD_MIN_OPTIONAL_RULES of the optional rules
 *      (lowercase, uppercase, number, special character).
 *
 * The length requirement is mandatory and is NOT counted as one of the optional
 * rules — that was the previous bug, where the 12-char rule was counted as just
 * another requirement alongside the character-type ones.
 */

export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 100
export const PASSWORD_MIN_OPTIONAL_RULES = 3

// Special characters accepted by the special-character rule. Shown to the user
// next to that requirement so they know which symbols are supported.
export const PASSWORD_SPECIAL_CHARS_DISPLAY = '! @ # $ % ^ & * ( ) , . ? " : { } | < >'

// Optional rules: the user must meet at least PASSWORD_MIN_OPTIONAL_RULES of them.
// `messageKey` maps to an i18n key under `$vuetify.<messageKey>`.
export const PASSWORD_OPTIONAL_RULES = [
    {key: 'lowercase', messageKey: 'passwordMinOneLowercase', regex: /[a-z]/},
    {key: 'uppercase', messageKey: 'passwordMinOneUppercase', regex: /[A-Z]/},
    {key: 'number', messageKey: 'passwordMinOneNumber', regex: /[0-9]/},
    {key: 'special', messageKey: 'passwordMinOneSpecialChar', regex: /[!@#$%^&*(),.?":{}|<>]/}
]

/**
 * Evaluates a password against the criteria.
 * @param {string} password
 * @returns {{
 *   lengthValid: boolean,
 *   optional: Array<{key: string, messageKey: string, valid: boolean}>,
 *   optionalMetCount: number,
 *   optionalValid: boolean,
 *   isValid: boolean
 * }}
 */
export function evaluatePassword(password) {
    const value = password || ''
    const lengthValid = value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH

    const optional = PASSWORD_OPTIONAL_RULES.map(rule => ({
        key: rule.key,
        messageKey: rule.messageKey,
        valid: rule.regex.test(value)
    }))

    const optionalMetCount = optional.filter(rule => rule.valid).length
    const optionalValid = optionalMetCount >= PASSWORD_MIN_OPTIONAL_RULES

    return {
        lengthValid,
        optional,
        optionalMetCount,
        optionalValid,
        isValid: lengthValid && optionalValid
    }
}
